import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, fileUrl } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { usePublicSettings } from "@/lib/settings";
import { Loader2, ImageOff, ShoppingCart, ChevronLeft, Truck, Package, Clock } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cart = useCart();
  const settings = usePublicSettings();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${slug}`)
      .then((res) => setProduct(res.data))
      .catch(() => toast.error("Product not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  const addToCart = () => {
    if (!product) return;
    cart.add(product, qty);
    toast.success(`Added · ${product.name} × ${qty}`);
  };

  const buyNow = () => {
    if (!product) return;
    cart.add(product, qty);
    navigate("/shop/cart");
  };

  return (
    <div className="min-h-screen bg-spark-floor">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-spark-slate hover:text-spark-teal mb-6">
          <ChevronLeft className="size-4" /> Back to shop
        </Link>
        {loading ? (
          <div className="text-center py-20"><Loader2 className="size-6 animate-spin inline text-spark-teal" /></div>
        ) : !product ? (
          <p className="text-center py-20 text-spark-slate">Product not found.</p>
        ) : (
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <div className="aspect-square rounded-md overflow-hidden bg-white border border-spark-line">
                {product.images?.[activeImg] ? (
                  <img src={fileUrl(product.images[activeImg].storage_path)} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-spark-line"><ImageOff className="size-14" /></div>
                )}
              </div>
              {product.images?.length > 1 && (
                <ul className="mt-3 grid grid-cols-5 gap-2">
                  {product.images.map((img, i) => (
                    <li key={img.id}>
                      <button
                        onClick={() => setActiveImg(i)}
                        className={`block w-full aspect-square rounded-md overflow-hidden border-2 ${activeImg === i ? "border-spark-teal" : "border-spark-line"}`}
                      >
                        <img src={fileUrl(img.storage_path)} alt="" className="w-full h-full object-cover" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="label-mono text-spark-teal">{product.category}</p>
              <h1 className="font-display uppercase tracking-tight text-3xl sm:text-4xl font-bold text-spark-ink mt-1.5" data-testid="product-name">{product.name}</h1>
              <p className="font-display uppercase tracking-tight text-3xl font-bold text-spark-ink mt-3">{money(product.price)}</p>
              {(() => {
                const isOnOrder = product.availability === "on_order";
                const outOfStock = !isOnOrder && product.track_stock && product.stock === 0;
                const lead = product.lead_time_days || 7;
                if (isOnOrder) {
                  return (
                    <p className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-400 text-spark-ink text-xs uppercase tracking-wider font-bold mt-2" data-testid="product-on-order">
                      <Clock className="size-3.5" /> Available on order · ships in ~{lead} days
                    </p>
                  );
                }
                if (product.track_stock) {
                  return (
                    <p className={`text-xs uppercase tracking-wider font-bold mt-1.5 ${outOfStock ? "text-red-600" : product.stock <= 3 ? "text-orange-600" : "text-emerald-700"}`}>
                      {outOfStock ? "Out of stock" : `${product.stock} in stock`}
                    </p>
                  );
                }
                return null;
              })()}

              <p className="text-spark-slate mt-5 leading-relaxed whitespace-pre-line">{product.description}</p>

              <div className="mt-6 grid sm:grid-cols-2 gap-3 text-xs">
                <div className="flex items-start gap-2 p-3 rounded-md bg-white border border-spark-line">
                  <Package className="size-4 text-spark-teal shrink-0 mt-0.5" />
                  <div>
                    <p className="font-display uppercase tracking-wider font-bold text-spark-ink">Free workshop pickup</p>
                    <p className="text-spark-slate">{settings.address_line ? `Collect from ${settings.address_line}.` : "Collect from the workshop."}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-3 rounded-md bg-white border border-spark-line">
                  <Truck className="size-4 text-spark-teal shrink-0 mt-0.5" />
                  <div>
                    <p className="font-display uppercase tracking-wider font-bold text-spark-ink">NZ shipping {money(product.shipping_fee)}</p>
                    <p className="text-spark-slate">Tracked courier, 2–4 working days.</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <div className="flex items-center border border-spark-line rounded-md overflow-hidden">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2.5 hover:bg-spark-floor font-display font-bold">−</button>
                  <span className="px-4 py-2.5 font-display font-bold tabular-nums" data-testid="product-qty">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="px-3 py-2.5 hover:bg-spark-floor font-display font-bold">+</button>
                </div>
                <button
                  onClick={addToCart}
                  disabled={product.availability !== "on_order" && product.track_stock && product.stock === 0}
                  className="btn-primary flex-1 disabled:opacity-40"
                  data-testid="product-add-to-cart"
                >
                  <ShoppingCart className="size-4" /> Add to cart
                </button>
              </div>
              <button
                onClick={buyNow}
                disabled={product.availability !== "on_order" && product.track_stock && product.stock === 0}
                className="btn-accent w-full mt-2 disabled:opacity-40"
                data-testid="product-buy-now"
              >
                Buy now
              </button>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ProductDetail;
